import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from "@material-ui/pickers";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import { setFilter, resetFilter } from "../../../../actions/projectmail";
import { initialSearchFilter } from "../../../../reducers/projectmail";
import { defaultPasscodeTheme } from "../../../../constants/passCodeUtils";
import LocalizedUtils, { moment } from "../../../../utils/LocalizedUtils";
import TableConfigurator from "../TableLayout/TableConfigurator";

const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  filterContainer: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    width: "100%",
    display: "inline-flex",
  },
  spaceButtons: {
    flex: "1",
    width: "10px",
  },
  patternValidate: {
    "&:invalid": {
      background: "#F08080",
    },
  },
}));

const FilterComponent = forwardRef((props, ref) => {
  const { filter, setFilterFn, resetPage, resetFilterFn, type, openFilter, translate, closeFilter } = props;

  const classes = useStyles();
  const [ownFilter, setOwnFilter] = useState(filter);

  useImperativeHandle(ref, () => ({
    setFilter: (filterValue, filterKey) => {
      setOwnFilter({ ...ownFilter, [filterKey]: filterValue });
    },
  }));

  useEffect(() => {
    if (filter === undefined) resetFilterFn(type);
    if (ownFilter === undefined && filter) setOwnFilter(filter);
  }, [filter, openFilter, resetFilterFn, type, ownFilter, setOwnFilter]);

  const [eventListener, setEventListener] = useState(undefined);
  useEffect(() => {
    const enterEvent = e => {
      if (e.key === "Enter" && openFilter === true) setFilterFn(ownFilter, type);
    };
    if (eventListener === undefined) {
      setEventListener(window.addEventListener("keydown", enterEvent));
    }
    return function cleanup() {
      window.removeEventListener("keydown", enterEvent);
      setEventListener(undefined);
    };
  }, [eventListener, setEventListener, ownFilter, setFilterFn, openFilter, type]);

  const handleTextChange = name => event => {
    setOwnFilter({ ...ownFilter, [name]: event.target.value });
  };

  const handleDateChange = (name, date) => {
    setOwnFilter({ ...ownFilter, [name]: new Date(moment.utc(date)).getTime() });
  };

  const textfield = (label, key) => {
    try {
      return (
        <TextField
          label={translate[TableConfigurator[key].label]}
          className={`${classes.textField} ${classes.lineExpander}`}
          value={ownFilter ? ownFilter[key] : ""}
          onChange={handleTextChange(key)}
          inputProps={{
            className: classes.patternValidate,
            pattern: TableConfigurator[key].filterProps ? TableConfigurator[key].filterProps : ".*",
          }}
        />
      );
    } catch {
      console.log(key);
      return null;
    }
  };

  return (
    <div className={classes.filterContainer}>
      {textfield(translate.title, "title")}
      {textfield(translate.customer, "customer")}
      <div className={classes.lineBreak} />
      <TextField
        label="Tag"
        className={`${classes.textField} ${classes.lineExpander}`}
        value={ownFilter ? ownFilter.tags : ""}
        onChange={handleTextChange("tags")}
      />
      {textfield(translate.EmailTicketSystemOwner, "owner")}
      {textfield(translate.operator, "operator")}
      <div className={classes.lineBreak} />
      {textfield(translate.additionalOperator, "additionalOperator")}
      {textfield(translate.orderProduct, "orderProduct")}
      {textfield(translate.externalId, "externalID")}
      <div className={classes.lineBreak} />
      <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
        <KeyboardDateTimePicker
          emptyLabel=""
          label={translate.eventStartTime}
          className={`${classes.textField} ${classes.lineExpander}`}
          ampm={false}
          format="DD.MM.YYYY"
          value={ownFilter ? ownFilter.eventStartTime : -1}
          onChange={date => handleDateChange("eventStartTime", date)}
        />
        <KeyboardDateTimePicker
          emptyLabel=""
          label={translate.eventEndTime}
          className={`${classes.textField} ${classes.lineExpander}`}
          ampm={false}
          format="DD.MM.YYYY"
          value={ownFilter ? ownFilter.eventEndTime : -1}
          onChange={date => handleDateChange("eventEndTime", date)}
        />
      </MuiPickersUtilsProvider>
      <div className={classes.lineBreak} />
      {textfield(translate.webcastID, "webcastID")}
      {textfield(translate.orderID, "orderID")}
      <div className={classes.lineBreak} />
      <Divider />
      <div className={classes.buttonContainer}>
        <div className={classes.spaceButtons} />
        <Button
          size="small"
          onClick={() => {
            resetFilterFn(type);
            setOwnFilter(initialSearchFilter);
            resetPage();
            closeFilter();
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() => {
            setFilterFn(ownFilter, type);
            resetPage();
          }}
        >
          Suche
        </Button>
      </div>
    </div>
  );
});

FilterComponent.defaultProps = {
  filter: undefined,
};

FilterComponent.propTypes = {
  filter: PropTypes.object,
  setFilterFn: PropTypes.func.isRequired,
  resetFilterFn: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  openFilter: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  resetPage: PropTypes.func.isRequired,
  closeFilter: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  filter: state.projectmail.searchFilter[ownProps.type],
  translate: state.ui.translate,
});

const mapDispatchToProps = {
  setFilterFn: (filter, type) => dispatch => dispatch(setFilter(filter, type)),
  resetFilterFn: type => dispatch => dispatch(resetFilter(type)),
};

const Filter = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(FilterComponent);

export default Filter;
